/**
 * Maps OGC Feature properties (CBS column names) to TypeScript types.
 * Handles sentinel values (-99995, -99997) used instead of null.
 */

import { AggregationLevel, BoundarySearchResult, Demographics } from '@/types/odin';

/** Dutch province name -> ODIN province code mapping */
export const PROVINCE_CODES: Record<string, string> = {
  'Groningen': 'GR', 'Friesland': 'FR', 'Drenthe': 'DR',
  'Overijssel': 'OV', 'Flevoland': 'FL', 'Gelderland': 'GE',
  'Utrecht': 'UT', 'Noord-Holland': 'NH', 'Zuid-Holland': 'ZH',
  'Zeeland': 'ZE', 'Noord-Brabant': 'NB', 'Limburg': 'LI',
  // Also support Fryslan variant
  'Fryslân': 'FR',
};

/** Extract midpoint from a distance range label like "5-10 km" -> 7.5 */
export function distanceMidpoint(label: string): number {
  const match = label.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);
  if (match) {
    return (Number(match[1]) + Number(match[2])) / 2;
  }
  // Single number fallback (e.g. "50+ km")
  const single = label.match(/(\d+(?:\.\d+)?)/);
  return single ? Number(single[1]) : 0;
}

// CBS sentinel values for suppressed/missing data
const CBS_SENTINELS = new Set([-99995, -99997, -99999]);

/** Clean a CBS value: replace sentinels with null */
export function cleanCbsValue(value: unknown): number | null {
  if (value == null) return null;
  const num = typeof value === 'number' ? value : Number(value);
  if (isNaN(num) || CBS_SENTINELS.has(num)) return null;
  return num;
}

/** Map collection IDs per aggregation level */
export function getCollectionId(level: AggregationLevel): string {
  const map: Record<AggregationLevel, string> = {
    pc4: 'boundaries-pc4',
    gemeente: 'boundaries-gemeente',
    provincie: 'boundaries-provincie',
  };
  return map[level];
}

/** Get the name property for each collection */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getNameProperty(_level: AggregationLevel): string {
  // All CBS boundary tables use 'statnaam' for the name
  return 'statnaam';
}

/** Get the code property for each collection */
export function getCodeProperty(level: AggregationLevel): string {
  if (level === 'pc4') return 'postcode';
  return 'statcode';
}

/** Map an OGC Feature to a BoundarySearchResult */
export function mapFeatureToSearchResult(
  feature: GeoJSON.Feature,
  level: AggregationLevel,
): BoundarySearchResult | null {
  const props = feature.properties;
  if (!props) return null;

  if (level === 'pc4') {
    const postcode = props.postcode;
    if (!postcode) return null;
    return {
      code: String(postcode),
      name: `PC4 ${postcode}`,
      level,
      population: cleanCbsValue(props.aantal_inwoners),
    };
  }

  const code = props.statcode;
  const name = props.statnaam;
  if (!code || !name) return null;

  return {
    code,
    name,
    level,
    population: level === 'gemeente' ? cleanCbsValue(props.aantal_inwoners) : null,
  };
}

/**
 * Map an OGC Feature to standardized properties for the map.
 * Returns { code, name, population } for use in feature.properties.
 */
export function mapFeatureForMap(
  feature: GeoJSON.Feature,
  level: AggregationLevel,
): { code: string; name: string; population?: number | null } | null {
  const props = feature.properties;
  if (!props) return null;

  if (level === 'pc4') {
    return {
      code: String(props.postcode ?? ''),
      name: `PC4 ${props.postcode ?? ''}`,
      population: cleanCbsValue(props.aantal_inwoners),
    };
  }

  return {
    code: props.statcode ?? '',
    name: props.statnaam ?? '',
    population: level === 'gemeente' ? cleanCbsValue(props.aantal_inwoners) : null,
  };
}

/**
 * Extract demographics from PC4 feature properties.
 * Only PC4 features have the full 138 CBS demographic fields.
 */
export function extractDemographics(props: Record<string, unknown>): Demographics {
  return {
    population: cleanCbsValue(props.aantal_inwoners),
    men: cleanCbsValue(props.aantal_mannen),
    women: cleanCbsValue(props.aantal_vrouwen),
    households: cleanCbsValue(props.aantal_part_huishoudens),
    dwellings: cleanCbsValue(props.aantal_woningen),
    avg_household_size: cleanCbsValue(props.gemiddelde_huishoudensgrootte),
    pct_owner_occupied: cleanCbsValue(props.percentage_koopwoningen),
    pct_rental: cleanCbsValue(props.percentage_huurwoningen),
    avg_woz_value: cleanCbsValue(props.gemiddelde_woz_waarde_woning),
    address_density: cleanCbsValue(props.omgevingsadressendichtheid),
  };
}

/**
 * Map ODIN mode labels (Dutch) to our modalSplit keys.
 */
export function mapOdinModeToSplit(
  modeDistribution: { labels: string[]; percentages: number[] },
): { car: number; bike: number; walk: number; transit: number; other: number } {
  const result = { car: 0, bike: 0, walk: 0, transit: 0, other: 0 };

  for (let i = 0; i < modeDistribution.labels.length; i++) {
    const label = modeDistribution.labels[i].toLowerCase();
    const pct = modeDistribution.percentages[i] ?? 0;

    if (label.includes('auto')) {
      result.car += pct;
    } else if (label.includes('fiets')) {
      result.bike += pct;
    } else if (label.includes('lopen') || label.includes('voet')) {
      result.walk += pct;
    } else if (label.includes('trein') || label.includes('btm') || label.includes('bus') || label.includes('tram') || label.includes('metro')) {
      result.transit += pct;
    } else {
      result.other += pct;
    }
  }

  return result;
}

/**
 * Extract top destinations from purpose distribution.
 */
export function mapOdinPurposeToDestinations(
  purposeDistribution: { labels: string[]; percentages: number[] },
): { name: string; percentage: number }[] {
  return purposeDistribution.labels
    .map((label, i) => ({
      name: label,
      percentage: purposeDistribution.percentages[i] ?? 0,
    }))
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 5);
}
